package com.iptvplayer.shared.data.remote.xtream

import kotlinx.serialization.KSerializer
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.MapSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.JsonDecoder
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

/**
 * Some Xtream panels send numeric fields (episode_num, season, rating_5based,
 * etc.) as JSON integers while others send them as strings. Either way, we
 * round-trip them as plain strings on our side.
 */
internal object IntOrStringAsString : KSerializer<String> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("IntOrStringAsString", PrimitiveKind.STRING)

    override fun deserialize(decoder: Decoder): String {
        return if (decoder is JsonDecoder) {
            val el = decoder.decodeJsonElement()
            (el as? JsonPrimitive)?.content ?: ""
        } else {
            decoder.decodeString()
        }
    }

    override fun serialize(encoder: Encoder, value: String) {
        encoder.encodeString(value)
    }
}

/**
 * `get_series_info` returns episodes as `{"1": [...], "2": [...]}` on most
 * panels, but a few panels send `[]` when a series has no episodes yet.
 * Coerce non-object payloads to an empty map so the whole series detail
 * doesn't fail to deserialize.
 */
internal object EpisodesMapSerializer : KSerializer<Map<String, List<XtreamEpisode>>> {
    private val mapSerializer = MapSerializer(String.serializer(), ListSerializer(XtreamEpisode.serializer()))

    override val descriptor: SerialDescriptor = mapSerializer.descriptor

    override fun deserialize(decoder: Decoder): Map<String, List<XtreamEpisode>> {
        if (decoder !is JsonDecoder) return mapSerializer.deserialize(decoder)
        val el = decoder.decodeJsonElement()
        return if (el is JsonObject) decoder.json.decodeFromJsonElement(mapSerializer, el) else emptyMap()
    }

    override fun serialize(encoder: Encoder, value: Map<String, List<XtreamEpisode>>) {
        mapSerializer.serialize(encoder, value)
    }
}
